import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useMainContext } from '@/context/main-context';
import { Eye, EyeOff, Loader } from 'lucide-react';
import { useState } from 'react';

export const description =
  "A login page with two columns. The first column has the login form with email and password. There's a Forgot your password link and a link to sign up if you do not have an account. The second column has a cover image.";

export const containerClassName = 'w-full h-full p-4 xl:p-0';

function Login() {
  const { signInUser } = useMainContext();
  const [data, setData] = useState({
    username: '',
    password: '',
  });
  const [isVisible, setIsVisible] = useState(false);
  const toggleVisibility = () => setIsVisible((prevState) => !prevState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData({
      ...data,
      [name]: value,
    });
  };

  const signIn = async () => {
    const email = `${data.username}@teacher.uz`;
    const password = data.password;

    if (!email || !password) {
      setError('Username yoki password kiritilmagan.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await signInUser(email, password);
    } catch (err) {
      setError('Failed to log in. Please check your username and password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full xl:grid xl:grid-cols-3 h-screen overflow-hidden">
      <div className="relative flex items-center justify-center h-screen xl:h-auto xl:py-12">
        <div className="absolute top-4 lg:top-8 mx-auto grid w-[350px] cursor-pointer hover:opacity-90 transition-opacity duration-300">
          <img
            className="w-20 lg:w-28 block dark:hidden"
            src="/assets/logos/studypro-black.svg"
            alt="StudyPro logo"
          />
          <img
            className="w-20 lg:w-28 hidden dark:block"
            src="/assets/logos/studypro-black.svg"
            alt="StudyPro logo"
          />
        </div>
        <div className="mx-auto grid w-[350px] gap-6">
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label required htmlFor="username">
                Username
              </Label>
              <div className="relative">
                <Input
                  className="peer pe-12 pr-24"
                  type="text"
                  id="username"
                  required
                  value={data.username}
                  onChange={handleChange}
                  name="username"
                  placeholder="johndoe"
                  disabled={loading}
                />
                <span className="pointer-events-none absolute inset-y-0 end-0 flex items-center justify-center pe-3 text-sm bg-background my-0.5 mr-0.5 rounded-r-md text-muted-foreground peer-disabled:opacity-50">
                  @teacher.uz
                </span>
              </div>
            </div>
            <div className="grid gap-2">
              <Label required htmlFor="password">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={isVisible ? 'text' : 'password'}
                  required
                  value={data.password}
                  onChange={handleChange}
                  name="password"
                  placeholder="***********"
                  disabled={loading}
                />
                <button
                  className="absolute inset-y-px end-px flex h-full w-9 items-center justify-center rounded-e-lg text-muted-foreground/80 transition-shadow hover:text-foreground focus-visible:border focus-visible:border-ring focus-visible:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                  type="button"
                  onClick={toggleVisibility}
                  disabled={loading}
                >
                  {isVisible ? (
                    <EyeOff size={16} strokeWidth={2} aria-hidden="true" />
                  ) : (
                    <Eye size={16} strokeWidth={2} aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>
            {error && <small className="text-red-500">{error}</small>}
            <Button className="w-full" onClick={signIn} disabled={loading}>
              {loading ? (
                <>
                  <Loader className="mr-2 h-4 w-4 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </Button>
          </div>
        </div>
      </div>
      <div className="hidden bg-muted xl:flex items-center justify-center col-span-2">
        <img
          className="h-auto w-[12rem] object-cover dark:brightness-[0.2] dark:grayscale"
          src="/assets/logos/studypro-black.svg"
          alt="StudyPro logo"
        />
      </div>
    </div>
  );
}

export default Login;
